import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  LinearProgress,
  Typography,
} from "@mui/material";
import {
  SensorsOutlined,
  BatteryChargingFullOutlined,
  MeetingRoomOutlined,
  WarningAmberRounded,
} from "@mui/icons-material";

const DETECTION_TIMEOUT_SEC = 20;

const BatteryNotDetectedDialog = ({
  open,
  doorNumber,
  batteryId,
  isDetected,
  batterySelect,
  isLoading,
  onSubmit,
}) => {
  const [elapsed, setElapsed] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!open) {
      setElapsed(0);
      setTimedOut(false);
      setConfirmed(false);
      return;
    }

    if (isDetected) return;

    const interval = setInterval(() => {
      setElapsed((prev) => {
        const next = prev + 1;
        if (next >= DETECTION_TIMEOUT_SEC) {
          setTimedOut(true);
          clearInterval(interval);
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open, isDetected]);

  const progress = Math.min((elapsed / DETECTION_TIMEOUT_SEC) * 100, 100);
  const remaining = Math.max(DETECTION_TIMEOUT_SEC - elapsed, 0);

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          bgcolor: timedOut ? "#FEF3F2" : "#F0FAFA",
          px: 3,
          pt: 2.5,
          pb: 2,
          borderBottom: "1px solid",
          borderColor: timedOut ? "#FECDCA" : "#B2E0E6",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: timedOut ? "#FEE4E2" : "#E0F7FA",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SensorsOutlined
              sx={{
                fontSize: 22,
                color: timedOut ? "#B42318" : "#0E7C86",
              }}
            />
          </Box>
          <Box>
            <Typography
              sx={{ fontSize: 16, fontWeight: 700, color: "#101828" }}
            >
              {timedOut ? "Battery not detected" : "Detecting battery..."}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#667085" }}>
              {timedOut
                ? "Station could not detect the battery"
                : "Waiting for station response"}
            </Typography>
          </Box>
        </Box>

        {!timedOut && !isDetected && (
          <Box sx={{ mt: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <Typography sx={{ fontSize: 11, color: "#667085" }}>
                Scanning...
              </Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  color: "#667085",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {remaining}s remaining
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: "#D0F0F4",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  bgcolor: "#0E7C86",
                },
              }}
            />
          </Box>
        )}
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Box
          sx={{
            p: 2,
            borderRadius: 2.5,
            bgcolor: "#F9FAFB",
            border: "1px solid #E3E8EE",
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <MeetingRoomOutlined sx={{ fontSize: 20, color: "#475467" }} />
              <Typography
                sx={{ fontSize: 14, fontWeight: 600, color: "#101828" }}
              >
                Door {doorNumber}
              </Typography>
            </Box>
            {!timedOut && !isDetected && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 999,
                  bgcolor: "#E0F7FA",
                  border: "1px solid #B2E0E6",
                }}
              >
                <CircularProgress size={12} sx={{ color: "#0E7C86" }} />
                <Typography
                  sx={{ fontSize: 11, color: "#0E7C86", fontWeight: 600 }}
                >
                  Scanning
                </Typography>
              </Box>
            )}
            {timedOut && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 999,
                  bgcolor: "#FEF3F2",
                  border: "1px solid #FECDCA",
                }}
              >
                <WarningAmberRounded sx={{ fontSize: 14, color: "#B42318" }} />
                <Typography
                  sx={{ fontSize: 11, color: "#B42318", fontWeight: 600 }}
                >
                  Not detected
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BatteryChargingFullOutlined
              sx={{ fontSize: 18, color: "#0E7C86" }}
            />
            <Box>
              <Typography sx={{ fontSize: 11, color: "#667085" }}>
                In-Battery ID
              </Typography>
              <Typography
                sx={{ fontSize: 13, fontWeight: 600, color: "#101828" }}
              >
                {batteryId || "—"}
              </Typography>
            </Box>
          </Box>
        </Box>

        {timedOut && (
          <>
            <Typography
              sx={{ fontSize: 13, fontWeight: 600, color: "#101828", mb: 1 }}
            >
              Select or confirm the in-battery
            </Typography>

            <Box sx={{ mb: 2 }}>{batterySelect}</Box>

            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: "#FFFCF5",
                border: "1px solid #FEF0C7",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <WarningAmberRounded
                  sx={{ fontSize: 18, color: "#DC6803", mt: 0.25 }}
                />
                <Typography
                  sx={{ fontSize: 12, color: "#93370D", lineHeight: 1.6 }}
                >
                  The station could not auto-detect the battery in Door{" "}
                  {doorNumber}. Please verify the battery ID is correct before
                  submitting.
                </Typography>
              </Box>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  sx={{
                    color: "#D0D5DD",
                    "&.Mui-checked": { color: "#0E7C86" },
                  }}
                />
              }
              label={
                <Typography
                  sx={{ fontSize: 13, color: "#344054", fontWeight: 500 }}
                >
                  I confirm the battery ID is correct and want to submit
                </Typography>
              }
            />
          </>
        )}

        {!timedOut && !isDetected && (
          <Typography sx={{ fontSize: 12, color: "#667085", lineHeight: 1.6 }}>
            The station is scanning for the battery inserted in Door{" "}
            {doorNumber}. Please wait for detection to complete. If the battery
            is not detected, you will be able to submit manually.
          </Typography>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 0,
        }}
      >
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={isLoading || !timedOut || !confirmed || !batteryId}
          fullWidth
          sx={{
            py: 1.25,
            fontWeight: 600,
            fontSize: 14,
            borderRadius: 2,
            bgcolor: "#0E7C86",
            "&:hover": { bgcolor: "#0A5F67" },
            "&.Mui-disabled": {
              bgcolor: "#D0D5DD",
              color: "#98A2B3",
            },
          }}
        >
          {isLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} sx={{ color: "#FFFFFF" }} />
              <span>Submitting...</span>
            </Box>
          ) : (
            "Submit swap"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BatteryNotDetectedDialog;
